import  { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Checkbox,
  Button,
  Divider,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  useColorModeValue,
  Flex,
  Icon,
  Badge,
  Stack,
  useToast,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { Info, Shield, FileText, AlertCircle } from 'lucide-react';
import { react_api } from '../../config';
import { useTranslation } from 'react-i18next';

export default function GuestAccessPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    typology: '',
    organization: '',
    motivation: '',
    accettazioneResponsabilita: false,
    accettazionePrivacy: false
  });

  const {t} = useTranslation();

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bgColor = useColorModeValue('#f8fffe', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('#e2e8f0', 'gray.600');
  const primaryColor = '#2d4a4f'; // Verde scuro GRINS
  const secondaryColor = '#4ecdc4'; // Verde acqua AMELIA
  const accentColor = '#8bc34a'; // Verde lime

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];

    if (!formData.name.trim()) {
      newErrors.name = 'Il nome è obbligatorio';
      missingFields.push('Nome');
    }
    if (!formData.surname.trim()) {
      newErrors.surname = 'Il cognome è obbligatorio';
      missingFields.push('Cognome');
    }
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email è obbligatoria';
      missingFields.push('Email');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Formato email non valido';
    }
    if (!formData.typology) {
      newErrors.typology = 'Seleziona il tuo ruolo';
      missingFields.push('Ruolo/Tipologia di Accesso');
    }
    if (!formData.motivation.trim()) {
      newErrors.motivation = 'La motivazione è obbligatoria';
      missingFields.push('Motivazione della Richiesta');
    }
    if (!formData.accettazioneResponsabilita) {
      newErrors.accettazioneResponsabilita = 'Devi accettare l\'assunzione di responsabilità';
      missingFields.push('Accettazione dell\'assunzione di responsabilità');
    }
    if (!formData.accettazionePrivacy) {
      newErrors.accettazionePrivacy = 'Devi accettare l\'informativa privacy';
      missingFields.push('Accettazione dell\'informativa privacy');
    }

    setErrors(newErrors);

    // Show toast with missing fields if validation fails
    if (missingFields.length > 0) {
      toast({
        title: "Campi mancanti",
        description: (
          <Box>
            <Text mb={2}>Per procedere devi completare i seguenti campi:</Text>
            <List spacing={1}>
              {missingFields.map((field, index) => (
                <ListItem key={index}>
                  <ListIcon as={AlertCircle} color="orange.500" boxSize={3} />
                  {field}
                </ListItem>
              ))}
            </List>
          </Box>
        ),
        status: "warning",
        duration: 8000,
        isClosable: true
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    
      const requestData = {
        email: formData.email,
        name: formData.name,
        surname: formData.surname,
        typology: formData.typology,
        organization: formData.organization,
        motivation: formData.motivation
      };

    
      const response = await axios.post(`${react_api}/admin/sendRegistrationRequest`, requestData);
        const result = response.data;
      if (result.status.toString() === "200") {
        
        toast({
          title: "Richiesta inviata con successo!",
          description:  t(`messageBE.${result.message}`),
          status: "success",
          duration: 4000,
          isClosable: true,
          onCloseComplete: () => {
            navigate('/');
          }
        });
        
        // Reset form
        setFormData({
          name: '',
          surname: '',
          email: '',
          typology: '',
          organization: '',
          motivation: '',
          accettazioneResponsabilita: false,
          accettazionePrivacy: false
        });
      }else {
      toast({
        title: "Error",
        description:  t(`messageBE.${result.message}`),
        status: "error",
        duration: 6000,
        isClosable: true
      });
      }

       setIsSubmitting(false);
    
  };

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="4xl">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading size="xl" mb={2} color={primaryColor}>
              Accesso Guest - Piattaforma AMELIA
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Accesso temporaneo alla piattaforma dati per analisi e modelli predittivi
            </Text>
          </Box>

          {/* Modalità Operative */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader pb={3}>
              <HStack>
                <Icon as={Info} color={secondaryColor} boxSize={5} />
                <Heading size="md" color={primaryColor}>Modalità Operative</Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={3}>
                <Text>
                  L'accesso guest alla piattaforma AMELIA è concesso esclusivamente per scopi di ricerca autorizzati 
                  e per un periodo limitato nel tempo, in conformità alle finalità della Fondazione GRINS.
                </Text>
                <Box>
                  <Text fontWeight="semibold" mb={2}>Condizioni di utilizzo della piattaforma:</Text>
                  <VStack align="start" spacing={1} pl={4}>
                    <Text fontSize="sm">• L'accesso è strettamente personale e non trasferibile</Text>
                    <Text fontSize="sm">• È vietato condividere le credenziali di accesso</Text>
                    <Text fontSize="sm">• Ogni attività sarà monitorata e registrata per finalità di sicurezza</Text>
                    <Text fontSize="sm">• L'accesso può essere revocato in qualsiasi momento</Text>
                    <Text fontSize="sm">• È necessario rispettare le policy di utilizzo dei dati AMELIA</Text>
                    <Text fontSize="sm">• I dati accessibili sono destinati esclusivamente a ricerca e analisi</Text>
                  </VStack>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Informativa Privacy */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader pb={3}>
              <HStack>
                <Icon as={Shield} color={secondaryColor} boxSize={5} />
                <Heading size="md" color={primaryColor}>Informativa Privacy</Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={3}>
                <Text>
                  I dati personali forniti saranno trattati dalla Fondazione GRINS in conformità al Regolamento Europeo 
                  sulla Protezione dei Dati (GDPR) e alle normative vigenti in materia di protezione dei dati.
                </Text>
                <Box>
                  <Text fontWeight="semibold" mb={2}>Finalità del trattamento:</Text>
                  <VStack align="start" spacing={1} pl={4}>
                    <Text fontSize="sm">• Gestione dell'accesso temporaneo alla piattaforma AMELIA</Text>
                    <Text fontSize="sm">• Monitoraggio delle attività per motivi di sicurezza e conformità</Text>
                    <Text fontSize="sm">• Adempimento di obblighi normativi e di trasparenza</Text>
                    <Text fontSize="sm">• Finalità di ricerca in ambito economico e sociale (ove applicabile)</Text>
                  </VStack>
                </Box>
                <Text fontSize="sm" color="gray.600">
                  I dati saranno conservati per il tempo strettamente necessario alle finalità indicate e 
                  successivamente cancellati secondo le procedure della Fondazione GRINS. 
                  Per maggiori informazioni: comunicazione@grinsfoundation.it
                </Text>
              </VStack>
            </CardBody>
          </Card>

          {/* Modulo di Assunzione di Responsabilità */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader pb={3}>
              <HStack>
                <Icon as={FileText} color={secondaryColor} boxSize={5} />
                <Heading size="md" color={primaryColor}>Modulo di Assunzione di Responsabilità</Heading>
                <Badge bg={accentColor} color="white" fontSize="xs">Obbligatorio</Badge>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <Box as="form" onSubmit={handleSubmit}>
                <VStack spacing={6} align="stretch">
                  
                  {/* Dati Personali */}
                  <Box>
                    <Heading size="sm" mb={4} color={primaryColor}>Dati Personali</Heading>
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                      <FormControl isRequired isInvalid={errors.name}>
                        <FormLabel>Nome</FormLabel>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Inserisci il tuo nome"
                        />
                        {errors.name && <Text color="red.500" fontSize="sm">{errors.name}</Text>}
                      </FormControl>

                      <FormControl isRequired isInvalid={errors.surname}>
                        <FormLabel>Cognome</FormLabel>
                        <Input
                          name="surname"
                          value={formData.surname}
                          onChange={handleInputChange}
                          placeholder="Inserisci il tuo cognome"
                        />
                        {errors.surname && <Text color="red.500" fontSize="sm">{errors.surname}</Text>}
                      </FormControl>
                    </Stack>
                  </Box>

                  {/* Contatti */}
                  <Box>
                    <Heading size="sm" mb={4} color={primaryColor}>Informazioni di Contatto</Heading>
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                      <FormControl isRequired isInvalid={errors.email}>
                        <FormLabel>Indirizzo Email</FormLabel>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="esempio@email.com"
                        />
                        {errors.email && <Text color="red.500" fontSize="sm">{errors.email}</Text>}
                      </FormControl>
                    </Stack>
                  </Box>

                  {/* Dettagli Accesso */}
                  <Box>
                    <Heading size="sm" mb={4} color={primaryColor}>Dettagli Accesso</Heading>
                    <Stack spacing={4}>
                      <FormControl isRequired isInvalid={errors.typology}>
                        <FormLabel>Ruolo/Tipologia di Accesso</FormLabel>
                        <Select
                          name="typology"
                          value={formData.typology}
                          onChange={handleInputChange}
                          placeholder="Seleziona il tuo ruolo"
                        >
                          <option value="Researcher">Researcher - Ricercatore</option>
                          <option value="Publisher">Publisher - Editore/Pubblicatore</option>
                          <option value="LcaManager">LCA Manager - Responsabile Tool File LCA</option>
                        </Select>
                        {errors.typology && <Text color="red.500" fontSize="sm">{errors.typology}</Text>}
                      </FormControl>

                      <FormControl>
                        <FormLabel>Ente/Istituzione di Appartenenza</FormLabel>
                        <Input
                          name="organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          placeholder="Università, ente di ricerca, istituzione, azienda..."
                        />
                      </FormControl>

                      <FormControl isRequired isInvalid={errors.motivation}>
                        <FormLabel>Motivazione della Richiesta e Finalità di Ricerca</FormLabel>
                        <Textarea
                          name="motivation"
                          value={formData.motivation}
                          onChange={handleInputChange}
                          placeholder="Descrivi il progetto di ricerca, le finalità dell'accesso e i risultati attesi..."
                          rows={4}
                        />
                        {errors.motivation && <Text color="red.500" fontSize="sm">{errors.motivation}</Text>}
                      </FormControl>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Accettazioni */}
                  <Box>
                    <Heading size="sm" mb={4} color={primaryColor}>Dichiarazioni e Accettazioni</Heading>
                    <VStack spacing={4} align="start">
                      
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <Box fontSize="sm">
                          <Text fontWeight="semibold">Assunzione di Responsabilità</Text>
                          <Text mt={1}>
                            Dichiari di essere consapevole che ogni azione compiuta durante l'accesso alla piattaforma AMELIA 
                            sarà registrata e di assumerti la piena responsabilità per l'uso appropriato dei dati e degli 
                            strumenti messi a disposizione dalla Fondazione GRINS per finalità di ricerca.
                          </Text>
                        </Box>
                      </Alert>

                      <FormControl isInvalid={errors.accettazioneResponsabilita}>
                        <Checkbox
                          name="accettazioneResponsabilita"
                          isChecked={formData.accettazioneResponsabilita}
                          onChange={handleInputChange}
                          colorScheme="teal"
                        >
                          <Text fontSize="sm">
                            <strong>Accetto l'assunzione di responsabilità</strong> e dichiaro di aver letto 
                            e compreso le modalità operative e le finalità di ricerca della piattaforma AMELIA
                          </Text>
                        </Checkbox>
                        {errors.accettazioneResponsabilita && (
                          <Text color="red.500" fontSize="sm">{errors.accettazioneResponsabilita}</Text>
                        )}
                      </FormControl>

                      <FormControl isInvalid={errors.accettazionePrivacy}>
                        <Checkbox
                          name="accettazionePrivacy"
                          isChecked={formData.accettazionePrivacy}
                          onChange={handleInputChange}
                          colorScheme="teal"
                        >
                          <Text fontSize="sm">
                            <strong>Accetto l'informativa privacy</strong> della Fondazione GRINS e autorizzo il trattamento 
                            dei miei dati personali per le finalità indicate
                          </Text>
                        </Checkbox>
                        {errors.accettazionePrivacy && (
                          <Text color="red.500" fontSize="sm">{errors.accettazionePrivacy}</Text>
                        )}
                      </FormControl>
                    </VStack>
                  </Box>

                  {/* Submit Button */}
                  <Flex justify="center" pt={4}>
                    <Button
                      type="submit"
                      size="lg"
                      isLoading={isSubmitting}
                      loadingText="Invio in corso..."
                      px={12}
                      bg={primaryColor}
                      color="white"
                      _hover={{ bg: secondaryColor }}
                      _active={{ bg: primaryColor }}
                    >
                      Invia Richiesta di Accesso a AMELIA
                    </Button>
                  </Flex>
                </VStack>
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
}